// @ts-nocheck
import * as React from "react";
import { Ic, Sidebar, Topbar, AppFooter, useCurrentUser, apiErrorMessage } from "./common";
import { CODManager } from "./cod-manager";
import { RejectMatchModal } from "../components/RejectMatchModal";
import { ConfirmMatchDecisionModal } from "../components/ConfirmMatchDecisionModal";
import { CaseInbox } from "../components/CaseInbox";
import { formatWait, classifyUrgency, URGENCY_ACCENT } from "../components/waitTime";
import { MATCH_DIMENSIONS, DEFAULT_WEIGHTS } from "../components/matchDimensions";
import { WeightAdjustModal } from "../components/WeightAdjustModal";
import { useKeyboardShortcuts } from "../components/useKeyboardShortcuts";
import { useMediaQuery, BREAKPOINTS } from "../components/useMediaQuery";
import { pushRecentReason } from "../components/recentReasons";
import { useEscapeKey } from "../components/useEscapeKey";
import { usePermissionsFromSession } from "../components/permissions";
import { getSeenSet, markSeen, pruneSeen } from "../components/seenCases";

// ─────────────────────────────────────────────────────────────────────────
// AI 자동매칭 (COD) — region admin decision tool
//
// Pulls one case + its top-N proposed matches from the backend, lets the
// operator accept / reject each candidate. Accepting cascades server-side
// (case → assigned, sibling matches → hold), so the client just needs to
// refetch on success.
//
// Component shape is intentionally flat: a stateful <COD> that owns data
// + actions, and several presentational children that read props only.
// Keeping the children pure means each one is trivially testable later
// without spinning up React state.
// ─────────────────────────────────────────────────────────────────────────

const RISK_BADGE = {
  critical: { color: "rose",  label: "위험" },
  warning:  { color: "amber", label: "주의" },
  stable:   { color: "green", label: "안정" },
};

const PRIORITY_BADGE = {
  critical: { color: "rose",  label: "높음 · 즉시 처리" },
  warning:  { color: "amber", label: "보통" },
  normal:   { color: "gray",  label: "낮음" },
};

// case_record.status → step index in the workflow stepper. Keeping the
// mapping in one place makes future status additions a single-line change.
const STATUS_STEP = {
  applying:   0,
  matching:   2,
  assigned:   3,
  caring:     4,
  closed:     4,
};

const STEPS = ["상태 입력", "AI 평가", "후보 추천", "배정 확정", "케어 개시"];

// Reuses MATCH_DIMENSIONS (imported) — keeps frontend in sync with the
// backend RECALC_DIMENSIONS constant. The legacy SCORE_DIMENSIONS alias is
// kept for the existing JSX render code referencing `key` as the sub-score
// field name (rather than the dimension key).
const SCORE_DIMENSIONS = MATCH_DIMENSIONS.map(d => ({
  key:     d.scoreKey,
  label:   d.label,
  metaFor: d.metaFor,
}));

// ─── API helper ──────────────────────────────────────────────────────────
// Centralizes the fetch + JSON + non-2xx → Error path so each call site
// reads as straight-line code. The thrown Error carries status so the UI
// can decide between 401/403/409 vs generic.
async function jsonFetch(url, opts = {}) {
  const res = await fetch(url, { credentials: "same-origin", ...opts });
  let body = null;
  try { body = await res.json(); } catch { /* non-JSON response */ }
  if (!res.ok) {
    const err = new Error(body?.error || body?.message || `HTTP ${res.status}`);
    err.status = res.status;
    err.body = body;
    err.code = body?.error;
    throw err;
  }
  return body;
}

function buildCodUrl(caseId) {
  return caseId ? `/api/v1/matches/latest_for_critical?case_id=${encodeURIComponent(caseId)}`
                : `/api/v1/matches/latest_for_critical`;
}

function readUrlCaseId() {
  return new URLSearchParams(window.location.search).get("case_id");
}

// Push case_id into the URL so a reload + a "share this case" link both
// keep the operator on the same row. history.replaceState avoids churning
// the back-button history with every inbox click.
function syncCaseIdToUrl(caseId) {
  const params = new URLSearchParams(window.location.search);
  if (caseId) params.set("case_id", String(caseId));
  else        params.delete("case_id");
  const next = `${window.location.pathname}${params.toString() ? "?" + params.toString() : ""}`;
  window.history.replaceState({}, "", next);
}

// Polling cadence for the inbox. 30s mirrors the region dashboard so the
// two screens feel coherent. Tab visibility pauses polling so a backgrounded
// COD tab doesn't keep hitting the API.
const QUEUE_POLL_MS = 30_000;
const TOAST_TTL_MS = 5_000;
// 한 폴링에 여러 건이 동시 도착해도 토스트가 한꺼번에 사라지지 않게, 도착 순서대로
// 닫힘 시점을 조금씩 늦춘다(위→아래 순차 소멸). 시차는 상한을 둬, 대량 도착 시에도
// 마지막 토스트가 과도하게 오래 남지 않게 한다(최대 +CAP*STEP ms).
const TOAST_STAGGER_MS = 600;
const TOAST_STAGGER_CAP = 6;

// ─────────────────────────────────────────────────────────────────────────
// Container
// ─────────────────────────────────────────────────────────────────────────

// 라우팅 래퍼만 담당. 운영자 흐름의 hook 들은 CODOperator 로 격리해서,
// 매니저 분기에서 조기 return 했을 때 hook 호출 순서가 흔들리는 React Rules
// of Hooks 위반(="Rendered fewer hooks than expected" → 흰 화면)을 막는다.
// 이전 구현은 useCurrentUser → 조건부 return → 그 아래에 20+ hook 가 있어
// userLoaded false→true 사이 hook 카운트가 달라져 트리 전체가 unmount 됐었다.
function COD() {
  const { user, loaded: userLoaded } = useCurrentUser();
  if (!userLoaded) {
    return <div className="app muted" style={{padding:40}}>불러오는 중…</div>;
  }
  // 매니저 역할은 권역 큐가 아니라 본인 매칭 인박스가 필요. 권한 모델도 분리됨
  // (`can_decide_matches` 본인만 / `can_manage_matches` 운영자 전용).
  if (user?.role === "manager") {
    return <CODManager/>;
  }
  return <CODOperator user={user} userLoaded={userLoaded}/>;
}

// AI 분석 연출의 단계 — 실제 매칭 점수 6차원(거리/자격/가용/부하/이력/평점)에 맞춰
// "연산 중"처럼 보이게 한다. 후보별 XAI 근거와 라벨이 일치해 신뢰감을 준다.
const ANALYZE_STEPS = [
  "활성 케어매니저 후보 스캔",
  "거리·이동시간 계산",
  "자격·전문성 매칭",
  "현재 부하·가용성 분석",
  "과거 이력·평점 종합",
];

// 후보를 즉시 노출하지 않고 보여주는 단계별 분석 패널(순수 시각 효과). 진행 단계(step)는
// 부모가 타이머로 올리고, 데이터는 별도로 fetch 된다(렌더 가드가 둘을 합류시킨다).
function CODThinking({ step, aiMeta }) {
  const total = ANALYZE_STEPS.length;
  const pct = Math.min(100, Math.round(((step + 1) / total) * 100));
  const ver = aiMeta?.model_version;
  return (
    <div className="card" style={{padding:'30px 26px'}}>
      <div style={{display:'flex', alignItems:'center', gap:14, marginBottom:18}}>
        <span className="cod-think-orb" aria-hidden="true">🧠</span>
        <div style={{minWidth:0}}>
          <div className="h3" style={{margin:0}}>AI가 후보를 분석하고 있습니다</div>
          <div className="muted" style={{fontSize:11.5, marginTop:2}}>
            적합도 모델{ver ? ` ${ver}` : ''} · 6개 지표로 케어매니저를 평가하는 중…
          </div>
        </div>
      </div>
      <div role="list" style={{display:'flex', flexDirection:'column', gap:11, marginBottom:20}}>
        {ANALYZE_STEPS.map((label, i) => {
          const st = i < step ? 'done' : i === step ? 'active' : 'pending';
          return (
            <div role="listitem" key={i} className={`cod-think-step cod-think-${st}`}>
              <span className="cod-think-mark" aria-hidden="true">{i < step ? '✓' : i === step ? '' : '·'}</span>
              <span className="cod-think-label">{label}</span>
              {i < step && <span className="cod-think-ok" aria-hidden="true">완료</span>}
            </div>
          );
        })}
      </div>
      <div className="cod-think-track" role="progressbar"
           aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label="AI 분석 진행률">
        <div className="cod-think-fill" style={{width:`${pct}%`}}/>
      </div>
    </div>
  );
}

function CODOperator({ user, userLoaded }) {
  const perms = usePermissionsFromSession({ user, loaded: userLoaded });
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [busyId, setBusyId] = React.useState(null);    // match id currently being acted on
  const [reject, setReject] = React.useState(null);    // match pending rejection, or null
  const [rejectErr, setRejectErr] = React.useState(null);
  // 배정/보류 확인 모달용 — null 또는 { match, decision }. 운영자가
  // 실수로 한 번에 결정하지 않게 한 단계 사이에 둔다(거절은 사유 모달에서
  // 이미 처리되므로 여기 대상이 아님).
  const [pendingDecision, setPendingDecision] = React.useState(null);
  const [pendingErr, setPendingErr] = React.useState(null);
  const [feedback, setFeedback] = React.useState(null); // { kind: 'success'|'error', text }

  // Inbox state
  const [queue, setQueue] = React.useState({ entries: [], total: 0 });
  // 현재 시스템 매칭 가중치(퍼센트) — 가중치 조정 모달의 초깃값. queue 응답에 동봉.
  const [policyWeights, setPolicyWeights] = React.useState(null);
  const [queueLoading, setQueueLoading] = React.useState(true);
  const [queueErr, setQueueErr] = React.useState(null);
  // Set of case ids previously seen — used to detect new arrivals across
  // polls. null seed means "first fetch, don't toast everything".
  const seenCaseIdsRef = React.useRef(null);
  // Stack of arrival toasts (caseId + name + dismiss timer id).
  const [toasts, setToasts] = React.useState([]);

  // selectedCaseId is the live source of truth; the URL is a side-effect.
  // Seed from ?case_id= so a refresh keeps you on the same row.
  const [selectedCaseId, setSelectedCaseId] = React.useState(readUrlCaseId);
  // 같은 케이스를 다시 눌러도 상세를 재조회시키는 트리거. selectedCaseId 가 그대로면
  // load 이펙트(deps: selectedCaseId)가 재실행되지 않아, onSelectCase 의 setData(null)
  // 만 남고 영구 "불러오는 중…" 에 갇혔다 — 매 선택마다 이 값을 증가시켜 재조회를 강제한다.
  const [loadNonce, setLoadNonce] = React.useState(0);
  // Focused match id for the XAI card. null falls back to "top match"
  // inside <XAICard/>, so we don't need to maintain it in lockstep with
  // the matches list when the case switches.
  const [focusedMatchId, setFocusedMatchId] = React.useState(null);
  // Weight preview modal open state.
  const [weightModalOpen, setWeightModalOpen] = React.useState(false);
  // Help overlay (keyboard shortcuts).
  const [helpOpen, setHelpOpen] = React.useState(false);
  // Polling auth state. When true, the queue effect short-circuits its
  // interval so we don't spam the API after the session has expired.
  const [authPaused, setAuthPaused] = React.useState(false);
  // Mobile-first inbox collapse. Defaults open on desktop; runs through a
  // toggle button on narrow screens so the operator can flip between
  // queue view and detail view.
  const isMobile = useMediaQuery(BREAKPOINTS.tablet, false);
  const [inboxOpen, setInboxOpen] = React.useState(!isMobile);
  // Inbox filter/search/seen state. priorityFilter generalises the old
  // emergencyOnly toggle (just pick "critical") and adds warning/normal.
  const [priorityFilter, setPriorityFilter] = React.useState("all");
  const [inboxQuery, setInboxQuery] = React.useState("");
  const [seenSet, setSeenSet] = React.useState(() => getSeenSet());
  // Resync inbox visibility when the viewport crosses the breakpoint —
  // resizing from mobile→desktop should reveal the inbox automatically.
  React.useEffect(() => { setInboxOpen(!isMobile); }, [isMobile]);

  const load = React.useCallback(() => {
    setErr(null);
    jsonFetch(buildCodUrl(selectedCaseId))
      .then(d => {
        setData(d);
        // Backend may have fallen back to "latest critical" — re-anchor
        // the URL + state to whichever case the server actually returned.
        if (d?.case?.id && String(d.case.id) !== String(selectedCaseId)) {
          setSelectedCaseId(String(d.case.id));
        }
      })
      .catch(e => setErr(apiErrorMessage(e)));
  }, [selectedCaseId]);

  // loadNonce 를 deps 에 둬, 같은 케이스 재선택(selectedCaseId 불변)에도 재조회가 돈다.
  // loadNonce 를 deps 에 둬, 같은 케이스 재선택(selectedCaseId 불변)에도 재조회가 돈다.
  React.useEffect(() => { load(); }, [load, loadNonce]);
  React.useEffect(() => { syncCaseIdToUrl(selectedCaseId); }, [selectedCaseId]);
  // Reset XAI focus + close the weight modal whenever the case changes —
  // a different case has a different match set.
  React.useEffect(() => {
    setFocusedMatchId(null);
    setWeightModalOpen(false);
  }, [selectedCaseId]);

  // ── AI 분석 연출 ─────────────────────────────────────────────────────
  // 후보를 즉시 띄우지 않고 ~2.6초간 단계별 분석 애니메이션을 보여준다(백엔드 무관,
  // 순수 연출). 실제 fetch 는 병렬로 돌고, 후보는 "애니메이션 종료 && 데이터 도착"
  // 시점에만 공개된다(렌더 가드의 analyzing 분기). 케이스 선택·후보 재생성에서 호출.
  const [analyzing, setAnalyzing] = React.useState(false);
  const [analyzeStep, setAnalyzeStep] = React.useState(0);
  const analyzeTimersRef = React.useRef([]);
  // 케이스 선택 연출은 세션당 1회만 — 리스트를 누를 때마다 반복되면 어색하므로
  // 첫 선택에만 보여준다(재생성은 명시적 재계산이라 매번 연출, 이 플래그와 무관).
  const selectAnimatedRef = React.useRef(false);
  const startAnalyze = React.useCallback(() => {
    analyzeTimersRef.current.forEach(clearTimeout);
    analyzeTimersRef.current = [];
    setAnalyzing(true);
    setAnalyzeStep(0);
    const stepMs = 1500;
    for (let i = 1; i < ANALYZE_STEPS.length; i++) {
      analyzeTimersRef.current.push(window.setTimeout(() => setAnalyzeStep(i), i * stepMs));
    }
    analyzeTimersRef.current.push(
      window.setTimeout(() => setAnalyzing(false), ANALYZE_STEPS.length * stepMs + 320),
    );
  }, []);
  React.useEffect(() => () => analyzeTimersRef.current.forEach(clearTimeout), []);

  // ── Queue fetch + polling ───────────────────────────────────────────
  const loadQueue = React.useCallback(() => {
    return jsonFetch("/api/v1/matches/queue")
      .then(j => {
        setQueue({ entries: j.cases || [], total: j.total ?? (j.cases || []).length });
        if (j.weights) setPolicyWeights(j.weights);
        setQueueErr(null);
        setAuthPaused(false);
        return j.cases || [];
      })
      .catch(e => {
        // 401 means session expired — stop polling so we don't blast the
        // login redirect endpoint, and surface a banner the user can act on.
        // Other errors keep polling; transient network issues recover on
        // their own and shouldn't block the inbox.
        if (e.status === 401) setAuthPaused(true);
        setQueueErr(apiErrorMessage(e));
        return [];
      })
      .finally(() => setQueueLoading(false));
  }, []);

  React.useEffect(() => {
    loadQueue();
    const tick = () => {
      if (authPaused) return;
      if (document.visibilityState !== "visible") return;
      loadQueue();
    };
    const intervalId = window.setInterval(tick, QUEUE_POLL_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [loadQueue, authPaused]);

  // Diff incoming queue entries — surface a toast for cases that weren't
  // in the previous snapshot. First fetch only seeds the set so the screen
  // doesn't open with the whole inbox flashing as "new".
  React.useEffect(() => {
    const ids = new Set(queue.entries.map(e => String(e.case.id)));
    if (seenCaseIdsRef.current === null) {
      seenCaseIdsRef.current = ids;
      return;
    }
    const prev = seenCaseIdsRef.current;
    const newcomers = queue.entries.filter(e => !prev.has(String(e.case.id)));
    seenCaseIdsRef.current = ids;
    // Trim storage to currently-visible cases so closed/deleted ids
    // don't accumulate over weeks of operation.
    pruneSeen(ids);
    if (!newcomers.length) return;
    newcomers.forEach((e, i) => {
      const tid = `${e.case.id}-${Date.now()}-${Math.random().toString(36).slice(2,7)}`;
      setToasts(prev => [...prev, {
        _tid: tid,
        caseId: e.case.id,
        elderlyName: e.case.elderly?.name || `사례 #${e.case.id}`,
        priority: e.case.priority,
      }]);
      // 도착 순서(i)대로 닫힘을 늦춰 순차 소멸. 시차는 CAP에서 포화.
      const stagger = Math.min(i, TOAST_STAGGER_CAP) * TOAST_STAGGER_MS;
      window.setTimeout(() => {
        setToasts(prev => prev.filter(x => x._tid !== tid));
      }, TOAST_TTL_MS + stagger);
    });
  }, [queue.entries]);

  const dismissToast = React.useCallback((tid) =>
    setToasts(prev => prev.filter(x => x._tid !== tid)), []);

  // Auto-clear the success/error banner so the workspace doesn't fill up
  // with stale messages during a busy review session.
  // 분석 연출 중에는 배너가 숨겨져 있으므로(아래 render 가드) 카운트다운을 보류한다 —
  // 안 그러면 연출(~7.8초) 도중 4초 타이머가 먼저 터져 연출이 끝났을 땐 이미 사라진다.
  // analyzing 이 false 가 되는(=배너가 보이기 시작하는) 시점부터 4초를 센다.
  React.useEffect(() => {
    if (!feedback || analyzing) return;
    const id = window.setTimeout(() => setFeedback(null), 4000);
    return () => window.clearTimeout(id);
  }, [feedback, analyzing]);

  const submitDecision = React.useCallback(async (match, decision, note) => {
    setBusyId(match.id);
    setRejectErr(null);
    setPendingErr(null);
    try {
      await jsonFetch(`/api/v1/matches/${match.id}/decide`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ decision, note: note || "" }),
      });
      setFeedback({
        kind: "success",
        text: decision === "accepted"
          ? `${match.care_manager.name} 매니저 배정이 완료되었습니다.`
          : `${match.care_manager.name} 후보를 ${decision === "rejected" ? "거절" : "보류"}했습니다.`,
      });
      // Persist the reason for next time so the modal can suggest it.
      // Storage-only — no PII, just the operator's prior input.
      if (decision === "rejected" && note) pushRecentReason("match_reject", note);
      setReject(null);
      setPendingDecision(null);
      // 인박스를 먼저 갱신해 다음 대기 케이스를 파악한다.
      const freshQueue = await loadQueue();
      if (decision === "accepted") {
        // 배정 확정 → 케이스가 매칭 풀에서 빠진다. 방금 배정한 케이스에 그대로 머물면
        // assigned 케이스의 후보(accepted+hold)가 계속 노출되므로(딥링크 ?case_id 로 재조회),
        // 다음 처리대기 케이스로 넘어가거나 — 없으면 선택을 비워(null) 기본(빈) 상태로 보낸다.
        // setData(null)로 방금 배정한 케이스의 후보를 즉시 치우고, selectedCaseId 변경이
        // 상세 재조회를 구동한다(여기서 load()를 직접 부르면 옛 selectedCaseId 로 assigned
        // 케이스를 다시 가져오는 레이스가 생기므로 부르지 않는다).
        const remaining = (freshQueue || []).filter(e => String(e.case.id) !== String(selectedCaseId));
        setData(null);
        setSelectedCaseId(remaining.length > 0 ? String(remaining[0].case.id) : null);
        setLoadNonce(n => n + 1);
      } else {
        // 거절/보류는 케이스가 매칭 상태로 남으므로 현재 상세만 갱신.
        load();
      }
    } catch (e) {
      const msg = e.status === 401 ? "로그인이 필요합니다."
                : e.status === 403 ? "결정 권한이 없습니다."
                : e.status === 409 ? "다른 작업으로 이미 처리된 매칭입니다. 새로고침해주세요."
                : e.message || "처리에 실패했습니다.";
      if (decision === "rejected")            setRejectErr(msg);
      else if (decision === "accepted" || decision === "hold") setPendingErr(msg);
      else                                    setFeedback({ kind: "error", text: msg });
    } finally {
      setBusyId(null);
    }
  }, [load, loadQueue, selectedCaseId]);

  // 보류 해제 — 보류된 후보를 다시 proposed로 되돌려 인박스 검토 대상으로
  // 복귀시킨다. 되돌릴 수 있는 가벼운 동작이라 확인 모달 없이 즉시 처리한다.
  const submitRelease = React.useCallback(async (match) => {
    setBusyId(match.id);
    setFeedback(null);
    try {
      await jsonFetch(`/api/v1/matches/${match.id}/release`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({}),
      });
      setFeedback({ kind: "success", text: `${match.care_manager.name} 후보의 보류를 해제했습니다.` });
      await Promise.all([Promise.resolve(load()), loadQueue()]);
    } catch (e) {
      const msg = e.status === 401 ? "로그인이 필요합니다."
                : e.status === 403 ? "결정 권한이 없습니다."
                : e.status === 409 ? "보류 상태가 아니거나 이미 배정된 케이스입니다. 새로고침해주세요."
                : e.message || "처리에 실패했습니다.";
      setFeedback({ kind: "error", text: msg });
    } finally {
      setBusyId(null);
    }
  }, [load, loadQueue]);

  // 배정/보류는 확인 모달을 한 번 거친다. 즉시 submitDecision을 호출하지
  // 않고 pendingDecision을 세팅해 모달을 띄운다.
  const onAccept   = React.useCallback((m) => { setPendingErr(null); setPendingDecision({ match: m, decision: "accepted" }); }, []);
  const onHold     = React.useCallback((m) => { setPendingErr(null); setPendingDecision({ match: m, decision: "hold" });     }, []);
  const onRelease  = React.useCallback((m) => submitRelease(m), [submitRelease]);
  const onReject   = React.useCallback(({ note }) => reject && submitDecision(reject, "rejected", note), [reject, submitDecision]);
  // 모달의 확정 버튼 — 메모를 그대로 백엔드 note로 전달.
  // 성공 시 submitDecision 내부에서 pendingDecision을 null로 비우고,
  // 실패 시 pendingErr이 세팅돼 모달이 그대로 유지된다.
  const onConfirmPending = React.useCallback(({ note }) => {
    if (!pendingDecision) return;
    const { match, decision } = pendingDecision;
    submitDecision(match, decision, note);
  }, [pendingDecision, submitDecision]);

  const onSelectCase = React.useCallback((id) => {
    setSelectedCaseId(String(id));
    setData(null); // force the spinner so it's obvious we're loading detail
    if (!selectAnimatedRef.current) {   // 세션 첫 선택에만 AI 분석 연출(이후는 즉시 후보)
      selectAnimatedRef.current = true;
      startAnalyze();
    }
    setLoadNonce(n => n + 1); // 같은 케이스 재클릭이어도 재조회를 강제(영구 스피너 방지)
    // Persist + reflect in local state so the NEW dot disappears immediately.
    markSeen(id);
    setSeenSet(prev => {
      const sid = String(id);
      if (prev.has(sid)) return prev;
      const next = new Set(prev);
      next.add(sid);
      return next;
    });
  }, [startAnalyze]);
  const onToastJump = React.useCallback((tid, caseId) => {
    dismissToast(tid);
    onSelectCase(caseId);
  }, [dismissToast, onSelectCase]);

  // Regenerate matches for the current case. POST returns the newly-created
  // rows; we refetch the whole case detail so the inserted matches show up
  // in rank order alongside the existing proposed/finalized ones.
  const [regenerating, setRegenerating] = React.useState(false);
  const regenerateMatches = React.useCallback(async () => {
    const id = data?.case?.id;
    if (!id || regenerating) return;
    setRegenerating(true);
    setFeedback(null);
    startAnalyze();   // 재생성도 분석 연출을 거쳐 새 후보를 공개
    try {
      const j = await jsonFetch("/api/v1/matches/regenerate", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ case_id: id }),
      });
      setFeedback({ kind: "success", text: `새 후보 ${j.created || 0}명을 추가했습니다.` });
      load();
    } catch (e) {
      const reason = e.body?.error;
      const msg =
        reason === "no_candidates" ? "더 이상 추천할 매니저가 없습니다." :
        reason === "no_managers"   ? "활성 매니저가 없어 후보를 만들 수 없습니다." :
        reason === "not_matching"  ? "이미 처리된 케이스라 재생성 불가합니다." :
        e.status === 403          ? "재생성 권한이 없습니다." :
        e.message || "후보 재생성 실패";
      setFeedback({ kind: "error", text: msg });
    } finally {
      setRegenerating(false);
    }
  }, [data?.case?.id, regenerating, load, startAnalyze]);

  // Persist a case-note edit. Optimistically updates the in-screen data
  // and rethrows so <ElderlyCard/> can surface the failure inline.
  const saveCaseNote = React.useCallback(async (newNote) => {
    const id = data?.case?.id;
    if (!id) return;
    await jsonFetch(`/api/v1/cases/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ case: { note: newNote } }),
    });
    setData(d => d ? { ...d, case: { ...d.case, note: newNote } } : d);
  }, [data?.case?.id]);

  // ── Keyboard shortcuts ──────────────────────────────────────────────
  // Derived from current data, so we read the freshest matches/queue
  // inside the handlers via refs to avoid re-binding on every render.
  const dataRef = React.useRef(data);
  const queueRef = React.useRef(queue);
  const focusedRef = React.useRef(focusedMatchId);
  React.useEffect(() => { dataRef.current = data; }, [data]);
  React.useEffect(() => { queueRef.current = queue; }, [queue]);
  React.useEffect(() => { focusedRef.current = focusedMatchId; }, [focusedMatchId]);

  // Resolve the match the action keys (a/r/h) target — the focused row if
  // any, otherwise the top candidate.
  const resolveTargetMatch = React.useCallback(() => {
    const matches = dataRef.current?.matches || [];
    if (!matches.length) return null;
    const focused = matches.find(m => m.id === focusedRef.current);
    return focused || matches[0];
  }, []);

  const moveInbox = React.useCallback((delta) => {
    const entries = queueRef.current.entries || [];
    if (entries.length === 0) return;
    const currentIdx = entries.findIndex(e => String(e.case.id) === String(selectedCaseId));
    const startIdx = currentIdx === -1 ? 0 : currentIdx;
    let nextIdx = startIdx + delta;
    nextIdx = Math.max(0, Math.min(entries.length - 1, nextIdx));
    const next = entries[nextIdx]?.case?.id;
    if (next != null) onSelectCase(next);
  }, [selectedCaseId, onSelectCase]);

  const focusByRank = React.useCallback((rank) => {
    const matches = dataRef.current?.matches || [];
    const m = matches[rank - 1];
    if (m) setFocusedMatchId(m.id);
  }, []);

  // Shortcuts are disabled while any modal is open; useEscapeKey inside
  // those modals already owns the keyboard during their lifetime.
  const shortcutsActive = !reject && !weightModalOpen && !helpOpen;

  const shortcuts = React.useMemo(() => ({
    "j":         () => moveInbox(+1),
    "k":         () => moveInbox(-1),
    "a":         () => { if (!perms.canDecideMatches) return; const m = resolveTargetMatch(); if (m && m.decision === "proposed") onAccept(m); },
    "r":         () => { if (!perms.canDecideMatches) return; const m = resolveTargetMatch(); if (m && m.decision === "proposed") { setRejectErr(null); setReject(m); } },
    "h":         () => { if (!perms.canDecideMatches) return; const m = resolveTargetMatch(); if (m && m.decision === "proposed") onHold(m); },
    "1":         () => focusByRank(1),
    "2":         () => focusByRank(2),
    "3":         () => focusByRank(3),
    "4":         () => focusByRank(4),
    "5":         () => focusByRank(5),
    "?":         () => setHelpOpen(open => !open),
    "/":         () => setHelpOpen(open => !open),
  }), [moveInbox, resolveTargetMatch, onAccept, onHold, focusByRank, perms.canDecideMatches]);

  useKeyboardShortcuts(shortcuts, shortcutsActive);

  // Wait time for the currently selected case (drives the detail header badge).
  const selectedWait = React.useMemo(() => {
    if (!data?.case) return null;
    const entry = queue.entries.find(e => String(e.case.id) === String(data.case.id));
    if (entry) return entry.waited_seconds;
    const createdAt = data.case.created_at ? new Date(data.case.created_at).getTime() : null;
    if (!createdAt) return null;
    return Math.floor((Date.now() - createdAt) / 1000);
  }, [data?.case, queue.entries]);

  const detailErr = err;
  const c = data?.case || null;
  const matches = data?.matches || [];

  return (
    <div className="app" style={{height:'100vh'}}>
      <div className="shell">
        <Sidebar role="region" active="cod"/>
        <div style={{display:'flex', flexDirection:'column', overflow:'hidden'}}>
          <Topbar
            title="AI 자동매칭 (COD)"
            sub={c?.elderly
              ? `${c.elderly.name} 어르신 · ${c.elderly.age}세 · 사례번호 C${String(c.id).padStart(5,'0')}`
              : '케이스 없음'}
          />
          <div className="main" style={{
            display:'grid',
            gridTemplateColumns: isMobile ? '1fr' : '260px 1fr',
            gap:14, alignItems:'start', minHeight:0, overflow:'auto',
          }}>

            {isMobile && (
              <MobileInboxToggle
                open={inboxOpen}
                onToggle={()=>setInboxOpen(o => !o)}
                count={queue.total ?? queue.entries.length}
              />
            )}

            {(inboxOpen || !isMobile) && (
              <CaseInbox
                entries={queue.entries}
                total={queue.total}
                selectedCaseId={selectedCaseId}
                loading={queueLoading}
                err={queueErr}
                onSelect={(id)=>{ if (isMobile) setInboxOpen(false); onSelectCase(id); }}
                onRefresh={loadQueue}
                priorityFilter={priorityFilter}
                onChangePriorityFilter={setPriorityFilter}
                query={inboxQuery}
                onChangeQuery={setInboxQuery}
                seenSet={seenSet}
              />
            )}

            <div style={{minWidth:0}}>
              {authPaused && <AuthPausedBanner onResume={()=>{ setAuthPaused(false); loadQueue(); }}/>}
              {/* 분석 연출 중엔 결과 배너를 숨긴다 — 재생성 결과(성공/실패)가 연출 도중
                  상단에 먼저 튀어나오던 문제. 연출이 끝나면(analyzing=false) 그때 노출. */}
              {feedback && !analyzing && <FeedbackBanner feedback={feedback} onClose={()=>setFeedback(null)}/>}

              {detailErr ? (
                <div className="card" style={{padding:'40px 22px', color:'var(--rose-500)', textAlign:'center'}}>
                  케이스 상세 불러오기 실패: {detailErr}
                </div>
              ) : analyzing ? (
                <>
                  {c && <StatusStepper status={c.status} waitedSeconds={selectedWait} priority={c.priority} assignedAt={c.assigned_at}/>}
                  <CODThinking step={analyzeStep} aiMeta={data?.ai_meta}/>
                </>
              ) : !c ? (
                // data===null: 아직 로딩 중. data 는 왔는데 case 가 null: 처리 대기(matching)
                // 케이스가 없음 — 배정/종결 케이스를 폴백으로 띄우지 않고 빈 상태로 안내한다.
                data === null ? (
                  <div className="card muted" style={{padding:'40px 22px', textAlign:'center'}}>불러오는 중…</div>
                ) : (
                  <div className="card" style={{padding:'48px 22px', textAlign:'center'}}>
                    <div style={{fontSize:34, marginBottom:10}} aria-hidden="true">✓</div>
                    <div style={{fontSize:15, fontWeight:700, color:'var(--ink-1)'}}>처리 대기 중인 케이스가 없습니다</div>
                    <div className="muted" style={{fontSize:12.5, marginTop:6, lineHeight:1.6}}>
                      매칭이 필요한 케이스가 모두 처리되었습니다.<br/>
                      새 케이스가 매칭 단계로 올라오면 왼쪽 처리 대기 목록에 표시됩니다.
                    </div>
                  </div>
                )
              ) : (
                <>
                  <StatusStepper status={c.status} waitedSeconds={selectedWait} priority={c.priority} assignedAt={c.assigned_at}/>

                  {matches.length === 0 ? (
                    <EmptyState
                      caseRecord={c}
                      candidatePool={data?.candidate_pool}
                      onRegenerate={regenerateMatches}
                      regenerating={regenerating}
                      canDecide={perms.canDecideMatches}
                    />
                  ) : (
                    <div style={{display:'grid',
                                 gridTemplateColumns: isMobile ? '1fr' : '320px 1fr',
                                 gap:14}}>
                      <div style={{display:'flex', flexDirection:'column', gap:14}}>
                        {c.elderly && (
                          <ElderlyCard
                            elderly={c.elderly}
                            caseNote={c.note}
                            caseId={c.id}
                            canEdit={perms.canManageCases}
                            onSaveNote={saveCaseNote}
                          />
                        )}
                        <AIEvaluationCard caseRecord={c}/>
                        <XAICard
                          matches={matches}
                          focusedId={focusedMatchId}
                          onFocus={setFocusedMatchId}
                          aiMeta={data.ai_meta}
                        />
                      </div>

                      <MatchCandidateList
                        matches={matches}
                        busyId={busyId}
                        onAccept={onAccept}
                        onReject={(m)=>{ setRejectErr(null); setReject(m); }}
                        onHold={onHold}
                        onRelease={onRelease}
                        onAdjustWeights={()=>setWeightModalOpen(true)}
                        onFocusMatch={setFocusedMatchId}
                        focusedMatchId={focusedMatchId}
                        compact={isMobile}
                        canDecide={perms.canDecideMatches}
                        onRegenerate={regenerateMatches}
                        regenerating={regenerating}
                        canRegenerate={c.status === 'matching'}
                      />
                    </div>
                  )}
                </>
              )}
            </div>

          </div>
          <AppFooter/>
        </div>
      </div>

      <ArrivalToasts toasts={toasts} onJump={onToastJump} onDismiss={dismissToast}/>

      {reject && (
        <RejectMatchModal
          match={reject}
          err={rejectErr}
          busy={busyId === reject.id}
          onClose={()=>{ setReject(null); setRejectErr(null); }}
          onConfirm={onReject}
        />
      )}

      {pendingDecision && (
        <ConfirmMatchDecisionModal
          match={pendingDecision.match}
          decision={pendingDecision.decision}
          err={pendingErr}
          busy={busyId === pendingDecision.match.id}
          onClose={()=>{ setPendingDecision(null); setPendingErr(null); }}
          onConfirm={onConfirmPending}
        />
      )}

      {weightModalOpen && c && (
        <WeightAdjustModal
          caseId={c.id}
          currentMatches={matches}
          initialWeights={policyWeights}
          canSavePolicy={user?.role === "system_admin"}
          onClose={()=>setWeightModalOpen(false)}
        />
      )}

      {helpOpen && <ShortcutHelpModal onClose={()=>setHelpOpen(false)}/>}

      {!isMobile && (
        <button type="button"
          onClick={()=>setHelpOpen(true)}
          title="키보드 단축키 (? 키)"
          aria-label="키보드 단축키"
          style={{position:'fixed', bottom:18, right:18, zIndex:40,
                  width:36, height:36, borderRadius:99,
                  background:'var(--surface)', border:'1px solid var(--border)',
                  boxShadow:'0 4px 14px rgba(0,0,0,0.12)',
                  fontWeight:800, fontSize:14, color:'var(--ink-2)', cursor:'pointer'}}>
          ?
        </button>
      )}
    </div>
  );
}

// ─── Phase 4 helper components ───────────────────────────────────────────

function MobileInboxToggle({ open, onToggle, count }) {
  return (
    <button type="button" onClick={onToggle}
      style={{width:'100%', padding:'10px 14px', border:'1px solid var(--border-soft)',
              background:'var(--surface)', borderRadius:10, display:'flex',
              alignItems:'center', justifyContent:'space-between', cursor:'pointer'}}>
      <span style={{fontWeight:700, fontSize:12.5}}>처리 대기 케이스 ({count}건)</span>
      <span className="muted" style={{fontSize:11}}>{open ? '접기 ▲' : '펼치기 ▼'}</span>
    </button>
  );
}

function AuthPausedBanner({ onResume }) {
  return (
    <div role="alert"
      style={{marginBottom:14, padding:'12px 16px', background:'var(--rose-50)',
              border:'1px solid var(--rose-100)', borderRadius:10,
              display:'flex', alignItems:'center', gap:12}}>
      <span style={{fontSize:16}}>⚠</span>
      <div style={{flex:1, fontSize:12.5, color:'var(--ink-2)'}}>
        <b style={{color:'var(--rose-500)'}}>세션이 만료되었습니다.</b>
        {' '}자동 새로고침을 멈췄습니다. 다시 로그인 후 재개해주세요.
      </div>
      <a href="/login" className="btn btn-ghost btn-sm" style={{textDecoration:'none'}}>로그인</a>
      <button type="button" className="btn btn-ghost btn-sm" onClick={onResume}>재시도</button>
    </div>
  );
}

const SHORTCUT_GROUPS = [
  {
    title: "인박스 탐색",
    items: [
      { keys: ["j"],         desc: "다음 케이스" },
      { keys: ["k"],         desc: "이전 케이스" },
    ],
  },
  {
    title: "후보 결정",
    items: [
      { keys: ["a"],         desc: "선택 후보 배정" },
      { keys: ["r"],         desc: "선택 후보 거절 (모달)" },
      { keys: ["h"],         desc: "선택 후보 보류" },
    ],
  },
  {
    title: "XAI 분석",
    items: [
      { keys: ["1","2","3","4","5"], desc: "N번째 후보로 분석 전환" },
    ],
  },
  {
    title: "기타",
    items: [
      { keys: ["?", "/"], desc: "단축키 도움말 토글" },
      { keys: ["Esc"],   desc: "모달/도움말 닫기" },
    ],
  },
];

function ShortcutHelpModal({ onClose }) {
  useEscapeKey(onClose);
  return (
    <div onClick={onClose}
      style={{position:'fixed', inset:0, background:'rgba(20,16,12,0.4)', display:'grid', placeItems:'center', zIndex:55}}>
      <div onClick={e=>e.stopPropagation()} className="card" style={{width:520, padding:0}}>
        <div style={{padding:'16px 22px', borderBottom:'1px solid var(--border-soft)', display:'flex', alignItems:'center', justifyContent:'space-between'}}>
          <div>
            <div className="h3">키보드 단축키</div>
            <div className="muted" style={{fontSize:11, marginTop:2}}>입력창 포커스 중에는 비활성화됩니다.</div>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>{Ic.close}</button>
        </div>
        <div style={{padding:22, display:'grid', gap:16}}>
          {SHORTCUT_GROUPS.map(group => (
            <div key={group.title}>
              <div className="muted" style={{fontSize:10.5, fontWeight:700, letterSpacing:'0.06em', marginBottom:6}}>
                {group.title}
              </div>
              <div style={{display:'flex', flexDirection:'column', gap:6}}>
                {group.items.map(item => (
                  <div key={item.desc} style={{display:'flex', alignItems:'center', gap:10}}>
                    <div style={{display:'flex', gap:4, minWidth:120}}>
                      {item.keys.map(k => <Kbd key={k}>{k}</Kbd>)}
                    </div>
                    <div style={{fontSize:12.5, color:'var(--ink-2)'}}>{item.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div style={{padding:'14px 22px', borderTop:'1px solid var(--border-soft)', display:'flex', justifyContent:'flex-end'}}>
          <button type="button" className="btn btn-ghost btn-md" onClick={onClose}>닫기</button>
        </div>
      </div>
    </div>
  );
}

function Kbd({ children }) {
  return (
    <span className="mono" style={{
      padding:'2px 8px', minWidth:24, textAlign:'center',
      background:'var(--bg-sub)', border:'1px solid var(--border-soft)',
      borderRadius:6, fontSize:11, fontWeight:700, color:'var(--ink-2)',
      boxShadow:'0 1px 0 var(--border)',
    }}>
      {children}
    </span>
  );
}

// ─── Presentational components ───────────────────────────────────────────

function ScreenMessage({ children, tone }) {
  const style = tone === "error"
    ? { padding: 40, color: "var(--rose-500)" }
    : { padding: 40 };
  return <div className={tone === "error" ? "app" : "app muted"} style={style}>{children}</div>;
}

function FeedbackBanner({ feedback, onClose }) {
  const ok = feedback.kind === "success";
  return (
    <div role="status" aria-live="polite"
      style={{
        marginBottom: 14,
        padding: '10px 14px',
        background: ok ? 'var(--green-50)' : 'var(--rose-50)',
        color:      ok ? 'var(--green-600)' : 'var(--rose-500)',
        borderRadius: 10,
        fontSize: 12.5,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}>
      <span>{ok ? '✓' : '⚠'}</span>
      <span style={{flex:1}}>{feedback.text}</span>
      <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} style={{padding:4}}>{Ic.close}</button>
    </div>
  );
}

// Freshness window for "방금 배정됨" — 5 minutes feels recent enough to
// matter to the operator without being so tight it never fires after a
// quick review.
const ASSIGNED_FRESH_MS = 5 * 60 * 1000;

function StatusStepper({ status, waitedSeconds, priority, assignedAt }) {
  const activeIdx = STATUS_STEP[status] ?? 2;
  const urgency = (waitedSeconds != null && priority)
    ? classifyUrgency(priority, waitedSeconds)
    : null;
  const accent = urgency ? URGENCY_ACCENT[urgency] : null;
  const justAssigned = (() => {
    if (!assignedAt || status !== "assigned") return false;
    const ts = new Date(assignedAt).getTime();
    if (Number.isNaN(ts)) return false;
    return (Date.now() - ts) <= ASSIGNED_FRESH_MS;
  })();
  return (
    <div className="card" style={{padding:'18px 24px', marginBottom:18, display:'flex', alignItems:'center', gap:16}}>
      {waitedSeconds != null && (
        <div style={{
          display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
          padding:'4px 14px', borderRight:'1px solid var(--border-soft)', marginRight:6,
        }}>
          <div className="muted" style={{fontSize:10, fontWeight:700, letterSpacing:'0.06em'}}>대기</div>
          <div className="mono" style={{fontSize:14, fontWeight:800, color: accent?.text || 'var(--ink-1)'}}>
            {formatWait(waitedSeconds)}
          </div>
        </div>
      )}
      {justAssigned && (
        <span className="badge badge-green" style={{
          padding:'4px 10px', fontSize:11, fontWeight:700,
          boxShadow:'0 0 0 3px var(--green-50)',
        }}>
          ✓ 방금 배정됨
        </span>
      )}
      <div style={{display:'flex', alignItems:'center', flex:1}}>
      {STEPS.map((label, i, arr) => {
        const done    = i < activeIdx;
        const current = i === activeIdx;
        return (
          <React.Fragment key={i}>
            <div style={{display:'flex', alignItems:'center', gap:10, flex:'0 0 auto'}}>
              <div style={{
                width: 30, height: 30, borderRadius: 99,
                background: done ? 'var(--green-500)' : current ? 'var(--orange-500)' : 'var(--bg-deep)',
                color:      done || current ? '#fff' : 'var(--ink-3)',
                display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 12,
              }}>
                {done ? <span style={{width:14,height:14,display:'inline-block'}}>{Ic.check}</span> : i + 1}
              </div>
              <div>
                <div style={{fontSize:13, fontWeight:700, color: current ? 'var(--orange-700)' : 'var(--ink-1)'}}>{label}</div>
                <div style={{fontSize:10, color:'var(--ink-3)'}}>
                  {done ? '완료' : current ? '현재' : '대기'}
                </div>
              </div>
            </div>
            {i < arr.length - 1 && (
              <div style={{flex:1, height:2, background: done ? 'var(--green-300)' : 'var(--border)', margin:'0 16px'}}/>
            )}
          </React.Fragment>
        );
      })}
      </div>
    </div>
  );
}

function ArrivalToasts({ toasts, onJump, onDismiss }) {
  if (!toasts.length) return null;
  return (
    <div role="region" aria-label="신규 매칭 알림" aria-live="polite"
      style={{position:'fixed', top:16, right:16, zIndex:60, display:'flex', flexDirection:'column', gap:8, maxWidth:340}}>
      {toasts.map(t => {
        const color = t.priority === 'critical' ? 'rose'
                    : t.priority === 'warning'  ? 'amber' : 'sky';
        return (
          <div key={t._tid} role="button" tabIndex={0}
            onClick={()=>onJump(t._tid, t.caseId)}
            onKeyDown={(e)=>{ if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onJump(t._tid, t.caseId); } }}
            style={{background:'var(--surface)', borderRadius:10, padding:'12px 14px', boxShadow:'0 6px 20px rgba(0,0,0,0.14)', cursor:'pointer', display:'flex', alignItems:'flex-start', gap:10, borderLeft:`4px solid var(--${color}-500)`, border:'1px solid var(--border-soft)'}}>
            <div style={{flex:1, minWidth:0}}>
              <div style={{fontSize:10.5, color:`var(--${color}-500)`, fontWeight:800, letterSpacing:0.2, marginBottom:3}}>
                새 매칭 대기
              </div>
              <div style={{fontSize:12.5, fontWeight:700}}>{t.elderlyName} 어르신</div>
              <div className="muted" style={{fontSize:11, marginTop:2}}>클릭하여 이동</div>
            </div>
            <button type="button" aria-label="닫기"
              onClick={(e)=>{ e.stopPropagation(); onDismiss(t._tid); }}
              style={{background:'transparent', border:'none', color:'var(--ink-3)', cursor:'pointer', padding:2, alignSelf:'flex-start'}}>
              {Ic.close}
            </button>
          </div>
        );
      })}
    </div>
  );
}

function ElderlyCard({ elderly, caseNote, caseId, canEdit = false, onSaveNote }) {
  const rb = RISK_BADGE[elderly.risk_level] || RISK_BADGE.stable;
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(caseNote || "");
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const textareaRef = React.useRef(null);

  // Sync draft when the upstream value changes (case switch). Avoid
  // stomping the operator's typing by only syncing when not editing.
  React.useEffect(() => {
    if (!editing) setDraft(caseNote || "");
  }, [caseNote, editing]);

  React.useEffect(() => {
    if (editing) textareaRef.current?.focus();
  }, [editing]);

  const startEdit = () => { setEditing(true); setErr(null); };
  const cancelEdit = () => { setDraft(caseNote || ""); setEditing(false); setErr(null); };
  const saveEdit = async () => {
    if (saving) return;
    setSaving(true); setErr(null);
    try {
      await onSaveNote?.(draft.trim());
      setEditing(false);
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card card-pad">
      <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:14}}>
        <div className="avatar lg">{elderly.name[0]}</div>
        <div style={{flex:1, minWidth:0}}>
          <div style={{fontSize:15, fontWeight:700}}>{elderly.name} 어르신</div>
          <div className="muted" style={{fontSize:11}}>{elderly.age}세 · {elderly.address}</div>
        </div>
        <span className={"badge badge-" + rb.color}>{rb.label}</span>
      </div>
      <div style={{padding:12, background:'var(--bg-sub)', borderRadius:8, fontSize:12.5, lineHeight:1.6}}>
        <b>건강상태:</b> {elderly.health_status || '정보 없음'}
      </div>

      {/* Case note section — editable when the operator has permission */}
      {editing ? (
        <div style={{marginTop:10}}>
          <textarea ref={textareaRef}
            className="input"
            rows={3}
            value={draft}
            onChange={e=>setDraft(e.target.value)}
            placeholder="케이스 운영 메모"
            style={{resize:'vertical', minHeight:60, fontSize:12}}/>
          {err && <div style={{marginTop:6, fontSize:11, color:'var(--rose-500)'}}>{err}</div>}
          <div style={{display:'flex', justifyContent:'flex-end', gap:6, marginTop:6}}>
            <button type="button" className="btn btn-ghost btn-sm" onClick={cancelEdit} disabled={saving}>취소</button>
            <button type="button" className="btn btn-primary btn-sm" onClick={saveEdit} disabled={saving}>
              {saving ? '저장 중…' : '저장'}
            </button>
          </div>
        </div>
      ) : caseNote ? (
        <div style={{marginTop:10, padding:10, background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:8, fontSize:12, display:'flex', gap:8, alignItems:'flex-start'}}>
          <div style={{flex:1, minWidth:0, whiteSpace:'pre-wrap'}}>{caseNote}</div>
          {canEdit && (
            <button type="button" onClick={startEdit}
              title="메모 편집"
              style={{background:'transparent', border:'none', color:'var(--rose-500)', cursor:'pointer', fontSize:11, fontWeight:700, padding:'0 4px'}}>
              편집
            </button>
          )}
        </div>
      ) : canEdit ? (
        <button type="button" onClick={startEdit}
          style={{marginTop:10, padding:10, fontSize:12, color:'var(--ink-3)', background:'var(--bg-sub)', border:'1px dashed var(--border)', borderRadius:8, width:'100%', textAlign:'left', cursor:'pointer'}}>
          + 케이스 메모 추가
        </button>
      ) : null}
    </div>
  );
}

// Maps recommender intensity (1-5) to a badge color. 5 = critical accent
// for "must do" services; 1-2 = muted for nice-to-have. Keeping the
// mapping local to the card so the recommender service stays purely
// data-shaped.
const INTENSITY_BADGE = {
  5: { color: "rose",   label: "필수" },
  4: { color: "orange", label: "권장" },
  3: { color: "amber",  label: "보통" },
  2: { color: "green",  label: "보조" },
  1: { color: "gray",   label: "선택" },
};

function AIEvaluationCard({ caseRecord }) {
  const pb = PRIORITY_BADGE[caseRecord.priority] || PRIORITY_BADGE.normal;
  const elderly = caseRecord.elderly;
  const services = caseRecord.recommended_services || [];
  return (
    <div className="card card-pad">
      <div className="h3" style={{marginBottom:10}}>AI 평가 결과</div>
      <div style={{padding:14, background:'linear-gradient(135deg, var(--orange-50), var(--bg-sub))', borderRadius:12, marginBottom:12}}>
        <div className="muted" style={{fontSize:11, fontWeight:600}}>우선순위</div>
        <div style={{fontSize:20, fontWeight:800, color:'var(--orange-700)', lineHeight:1.2, margin:'4px 0'}}>
          {pb.label}
        </div>
        {elderly?.risk_level && (
          <div className="muted" style={{fontSize:11}}>
            노쇠도 {RISK_BADGE[elderly.risk_level]?.label || elderly.risk_level}
          </div>
        )}
      </div>

      <div className="muted" style={{fontSize:10.5, fontWeight:700, letterSpacing:'0.04em', marginBottom:8}}>
        권장 서비스 ({services.length}건)
      </div>

      {services.length === 0 ? (
        <div className="muted" style={{fontSize:11, lineHeight:1.5}}>
          이 케이스에 추천된 서비스가 없습니다.
        </div>
      ) : (
        <div style={{display:'flex', flexDirection:'column', gap:6}}>
          {services.map(s => {
            const ib = INTENSITY_BADGE[s.intensity] || INTENSITY_BADGE[3];
            return (
              <div key={s.kind}
                title={s.rationale || ''}
                style={{display:'flex', alignItems:'flex-start', gap:8, padding:'6px 8px', background:'var(--bg-sub)', borderRadius:8}}>
                <span className={"badge badge-" + ib.color} style={{flex:'0 0 auto', fontSize:9.5, padding:'1px 6px', minWidth:32, textAlign:'center'}}>
                  {ib.label}
                </span>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontSize:11.5, fontWeight:700, color:'var(--ink-1)'}}>{s.label}</div>
                  {s.rationale && (
                    <div className="muted" style={{fontSize:10.5, marginTop:1}}>
                      {s.rationale}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// XAI card now reads from the currently-focused match instead of always
// pinning to the top candidate. Defaults to the first match; switching
// focuses any candidate so the operator can read why each was scored.
function XAICard({ matches, focusedId, onFocus, aiMeta }) {
  const focused = React.useMemo(
    () => matches.find(m => m.id === focusedId) || matches[0] || null,
    [matches, focusedId]
  );
  if (!focused) {
    return (
      <div className="card card-pad" style={{background:'var(--bg-sub)', borderStyle:'dashed'}}>
        <XAIHeader/>
        <div className="muted" style={{fontSize:11.5}}>평가 데이터 부족</div>
      </div>
    );
  }

  // Backend XAI payload (Matches::XAIExplainer). Fallback to legacy
  // reasoning + score grid when an older response shape comes through.
  const xai = focused.xai;

  return (
    <div className="card card-pad" style={{background:'var(--bg-sub)', borderStyle:'dashed'}}>
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, marginBottom:10}}>
        <XAIHeader/>
        {matches.length > 1 && (
          <select
            className="select"
            value={focused.id}
            onChange={e => onFocus(Number(e.target.value))}
            aria-label="평가할 후보 선택"
            style={{width:140, fontSize:11}}
          >
            {matches.map(m => (
              <option key={m.id} value={m.id}>
                {m.rank}. {m.care_manager.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {xai ? (
        <>
          {xai.summary && <XAISummary summary={xai.summary} tier={xai.overall_tier}/>}
          {xai.dimensions && <XAIDimensionList dimensions={xai.dimensions}/>}
        </>
      ) : (
        // Backwards-compatible fallback — keeps the card useful if a stale
        // client talks to a new server (or vice versa).
        <>
          <div style={{fontSize:11.5, color:'var(--ink-2)', lineHeight:1.65, marginBottom:10}}>
            {focused.reasoning || '평가 데이터 부족'}
          </div>
          <div style={{borderTop:'1px solid var(--border-soft)', paddingTop:8}}>
            <div className="muted" style={{fontSize:10, fontWeight:700, letterSpacing:'0.04em', marginBottom:6}}>점수 분해</div>
            <div style={{display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:6}}>
              {MATCH_DIMENSIONS.map(d => {
                const v = focused[d.scoreKey] ?? 0;
                return (
                  <div key={d.key} style={{display:'flex', alignItems:'baseline', justifyContent:'space-between', fontSize:10.5}}>
                    <span style={{color:'var(--ink-3)'}}>{d.label}</span>
                    <span className="mono" style={{fontWeight:700,
                      color: v >= 90 ? 'var(--green-600)' : v >= 80 ? 'var(--orange-600)' : 'var(--ink-2)'}}>
                      {v}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {aiMeta && <AIMetaFooter meta={aiMeta}/>}
    </div>
  );
}

// ── XAI sub-components ─────────────────────────────────────────────────

function XAISummary({ summary, tier }) {
  if (!tier) return null;
  return (
    <div style={{
      padding: '10px 12px',
      borderLeft: `3px solid var(--${tier.color}-500)`,
      background: `var(--${tier.color}-50)`,
      borderRadius: 8,
      marginBottom: 12,
    }}>
      <div style={{display:'flex', alignItems:'center', gap:6, marginBottom:4}}>
        <StarRating stars={tier.stars} color={tier.color}/>
        <span style={{fontSize:11.5, fontWeight:800, color:`var(--${tier.color}-700)`}}>
          {tier.label}
        </span>
      </div>
      <div style={{fontSize:11, lineHeight:1.6, color:'var(--ink-1)'}}>
        {summary}
      </div>
    </div>
  );
}

function XAIDimensionList({ dimensions }) {
  return (
    <div style={{paddingTop:2}}>
      <div className="muted" style={{fontSize:10, fontWeight:700, letterSpacing:'0.04em', marginBottom:2}}>
        차원별 분석
      </div>
      {dimensions.map(d => <XAIDimensionRow key={d.key} dim={d}/>)}
    </div>
  );
}

function XAIDimensionRow({ dim }) {
  const t = dim.tier || {};
  return (
    <div style={{padding:'8px 0', borderTop:'1px solid var(--border-soft)'}}>
      <div style={{display:'flex', alignItems:'center', gap:6, marginBottom:4, flexWrap:'wrap'}}>
        <span style={{width:6, height:6, borderRadius:99, background:`var(--${t.color}-500)`, display:'inline-block', flex:'0 0 auto'}}/>
        <span style={{fontSize:11.5, fontWeight:700, flex:1, minWidth:60}}>{dim.label}</span>
        <span className="mono" style={{fontSize:13, fontWeight:800, color:`var(--${t.color}-600)`}}>
          {dim.score}
        </span>
        <StarRating stars={t.stars} color={t.color} small/>
        <span className={"badge badge-" + t.color} style={{fontSize:9, padding:'1px 6px', whiteSpace:'nowrap'}}>
          {t.label}
        </span>
      </div>
      <div style={{fontSize:11, color:'var(--ink-2)', lineHeight:1.5, marginBottom:3, paddingLeft:12}}>
        {dim.phrase}
      </div>
      <div className="muted" style={{fontSize:10, lineHeight:1.4, paddingLeft:12, display:'flex', gap:4}}>
        <span style={{color:'var(--ink-4)'}}>└</span>
        <span>{dim.evidence}</span>
      </div>
    </div>
  );
}

// 5칸 별점. stars=N개 채우고 나머지는 흐릿하게. small=true면 폰트 11px.
function StarRating({ stars = 0, color = "ink-3", small = false }) {
  const size = small ? 11 : 13;
  return (
    <span aria-label={`${stars}점 등급`} role="img"
      style={{display:'inline-flex', gap:1, fontSize:size, color:`var(--${color}-500)`, letterSpacing:0.5, lineHeight:1, flex:'0 0 auto'}}>
      {[0,1,2,3,4].map(i => (
        <span key={i} style={{opacity: i < stars ? 1 : 0.22}}>★</span>
      ))}
    </span>
  );
}

function AIMetaFooter({ meta }) {
  const trained = meta.trained_at ? new Date(meta.trained_at).toLocaleDateString('ko-KR', { year:'numeric', month:'2-digit' }) : '—';
  const confidence = meta.confidence;
  const confColor = confidence >= 90 ? 'var(--green-600)'
                  : confidence >= 75 ? 'var(--orange-600)'
                  :                    'var(--rose-500)';
  return (
    <div style={{marginTop:10, paddingTop:8, borderTop:'1px solid var(--border-soft)'}}>
      <div className="muted" style={{fontSize:10, fontWeight:700, letterSpacing:'0.04em', marginBottom:6}}>모델 정보</div>
      <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:4, fontSize:10.5, color:'var(--ink-3)'}}>
        <span>모델</span>
        <span className="mono" style={{color:'var(--ink-2)', textAlign:'right'}}>{meta.model_version}</span>
        <span>학습 데이터</span>
        <span className="mono" style={{color:'var(--ink-2)', textAlign:'right'}}>{trained} · {meta.base_samples?.toLocaleString?.() || meta.base_samples}건</span>
        {meta.confidence != null && (
          <>
            <span>이 케이스 신뢰도</span>
            <span className="mono" style={{color: confColor, textAlign:'right', fontWeight:800}}>{meta.confidence}%</span>
          </>
        )}
        {meta.update_cycle && (
          <>
            <span>업데이트</span>
            <span className="mono" style={{color:'var(--ink-2)', textAlign:'right', fontSize:10}}>{meta.update_cycle}</span>
          </>
        )}
      </div>
    </div>
  );
}

function XAIHeader() {
  return (
    <div style={{display:'flex', alignItems:'center', gap:8}}>
      <span style={{width:22, height:22, borderRadius:6, background:'var(--orange-500)', color:'#fff', display:'grid', placeItems:'center', fontWeight:800, fontSize:9}}>AI</span>
      <div style={{fontSize:12, fontWeight:700}}>판단 근거 (XAI)</div>
    </div>
  );
}

function MatchCandidateList({ matches, busyId, onAccept, onReject, onHold, onRelease, onAdjustWeights, onFocusMatch, focusedMatchId, compact = false, canDecide = true, onRegenerate, regenerating = false, canRegenerate = true }) {
  const orgs = React.useMemo(() => aggregateOrgs(matches), [matches]);
  return (
    <div className="card">
      <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 22px 12px', gap:8}}>
        <div style={{minWidth:0}}>
          <div className="h3">추천 후보 매니저 ({matches.length}명)</div>
          <div className="muted" style={{fontSize:11, marginTop:2}}>
            가중치: 거리 · 자격 · 가용 · 부하 · 과거관계 · 평가
          </div>
        </div>
        {canDecide && (
          <div style={{display:'flex', gap:6, flexWrap:'wrap', justifyContent:'flex-end'}}>
            {onRegenerate && canRegenerate && (
              <button type="button" className="btn btn-ghost btn-sm"
                onClick={onRegenerate}
                disabled={regenerating}
                title="이 케이스의 매칭 후보를 다시 계산해 추가합니다"
                style={{whiteSpace:'nowrap'}}>
                {regenerating ? '생성 중…' : '후보 재생성 ↻'}
              </button>
            )}
            <button type="button" className="btn btn-ghost btn-sm"
              onClick={onAdjustWeights}
              title="가중치를 임시로 조정해 순위 변화를 미리 봅니다"
              style={{whiteSpace:'nowrap'}}>
              {Ic.filter} 가중치 조정
            </button>
          </div>
        )}
      </div>

      {!canDecide && (
        <div className="muted" style={{padding:'0 22px 12px', fontSize:11.5}}>
          ⓘ 매칭 결정은 권역 관리자만 수행할 수 있습니다. 점수 분석은 자유롭게 열람할 수 있습니다.
        </div>
      )}

      {matches.map((m, i) => (
        // 래퍼에 stagger 공개 애니메이션 — 행 자체 opacity(finalized dimmed)와 곱해져
        // 흐림 상태를 유지하면서 위에서부터 순차로 드러난다. 신규 mount 시에만 1회 재생.
        <div key={m.id} className="cod-reveal" style={{animationDelay: `${Math.min(i, 8) * 55}ms`}}>
          <MatchRow
            match={m}
            busy={busyId === m.id}
            focused={focusedMatchId === m.id}
            onAccept={onAccept}
            onReject={onReject}
            onHold={onHold}
            onRelease={onRelease}
            onFocus={onFocusMatch}
            compact={compact}
            canDecide={canDecide}
          />
        </div>
      ))}

      {orgs.length > 0 && <OrgRecommendations orgs={orgs}/>}
    </div>
  );
}

function MatchRow({ match, busy, focused, onAccept, onReject, onHold, onRelease, onFocus, compact = false, canDecide = true }) {
  const top = match.rank === 1;
  const cm  = match.care_manager;
  const finalized = match.decision !== "proposed";
  const decidedAtLabel = match.decided_at
    ? new Date(match.decided_at).toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    : null;
  const decided = {
    accepted: { label: "배정 완료", color: "green" },
    rejected: { label: "거절됨",   color: "rose"  },
    hold:     { label: "보류",     color: "gray"  },
  }[match.decision];

  return (
    <div style={{
      padding: '16px 22px',
      borderTop: '1px solid var(--border-soft)',
      background: focused
        ? 'var(--orange-50)'
        : top && !finalized
          ? 'linear-gradient(90deg, var(--orange-50), transparent)'
          : 'transparent',
      position: 'relative',
      opacity: finalized && match.decision !== "accepted" ? 0.55 : 1,
      transition: 'background 0.12s',
    }}>
      {top && !finalized && <span style={{position:'absolute', left:0, top:0, bottom:0, width:3, background:'var(--orange-500)'}}/>}

      <div style={{display:'flex', alignItems:'center', gap:14, marginBottom:12}}>
        <button type="button"
          onClick={()=>onFocus && onFocus(match.id)}
          title="이 후보를 XAI 카드에서 분석"
          aria-pressed={focused}
          aria-label={`${cm.name} 후보 XAI 분석`}
          style={{display:'flex', alignItems:'center', gap:14, flex:1, minWidth:0,
                  background:'transparent', border:'none', padding:0, cursor: onFocus ? 'pointer' : 'default',
                  textAlign:'left'}}>
          <div className={"avatar lg " + (top ? '' : 'green')}>{cm.name[0]}</div>
          <div style={{flex:1, minWidth:0}}>
            <div style={{display:'flex', alignItems:'center', gap:8, flexWrap:'wrap'}}>
              <div style={{fontSize:15, fontWeight:700}}>{cm.name}</div>
              <span className="muted" style={{fontSize:11}}>· {cm.qualification}</span>
              {top && !finalized && <span className="badge badge-orange">최우선</span>}
              {decided && (
                <span className={"badge badge-" + decided.color}
                  title={decidedAtLabel ? `결정 시각: ${decidedAtLabel}` : undefined}>
                  {decided.label}{decidedAtLabel ? ` · ${decidedAtLabel}` : ''}
                </span>
              )}
            </div>
            <div className="muted" style={{fontSize:11}}>{cm.organization_name || '소속 미배정'}</div>
          </div>
        </button>
        {cm.id && (
          <a href={`/managers?id=${cm.id}`}
             onClick={(e)=>e.stopPropagation()}
             title={`${cm.name} 매니저 프로필 보기`}
             aria-label={`${cm.name} 매니저 프로필 보기`}
             style={{padding:'4px 8px', fontSize:11, fontWeight:600,
                     color:'var(--ink-3)', textDecoration:'none',
                     border:'1px solid var(--border-soft)', borderRadius:6,
                     whiteSpace:'nowrap'}}>
            프로필 ↗
          </a>
        )}
        <div style={{textAlign:'right'}}>
          <div className="mono" style={{fontSize:30, fontWeight:800,
            color: top ? 'var(--orange-600)' : match.rank === 2 ? 'var(--green-600)' : 'var(--amber-500)',
            lineHeight: 1}}>{match.score}</div>
          <div className="muted" style={{fontSize:9, fontWeight:700, letterSpacing:'0.1em'}}>MATCH</div>
        </div>
        <div style={{display:'flex', gap:6}}>
          {!finalized && canDecide && (
            <>
              <button type="button" className={"btn " + (top ? 'btn-primary' : 'btn-ghost') + " btn-md"}
                onClick={()=>onAccept(match)} disabled={busy}>
                {busy ? '처리 중…' : '배정 →'}
              </button>
              <button type="button" className="btn btn-ghost btn-md"
                onClick={()=>onReject(match)} disabled={busy}
                style={{color:'var(--rose-500)'}}>
                거절
              </button>
              <button type="button" className="btn btn-ghost btn-md"
                onClick={()=>onHold(match)} disabled={busy}
                title="이번 케이스에서 일단 보류"
                style={{color:'var(--ink-3)'}}>
                보류
              </button>
            </>
          )}
          {match.decision === "hold" && canDecide && onRelease && (
            <button type="button" className="btn btn-ghost btn-md"
              onClick={()=>onRelease(match)} disabled={busy}
              title="보류를 해제하고 다시 검토 후보로 되돌립니다"
              style={{color:'var(--orange-600)'}}>
              {busy ? '처리 중…' : '보류 해제'}
            </button>
          )}
        </div>
      </div>

      <div style={{display:'grid', gridTemplateColumns: compact ? 'repeat(3,1fr)' : 'repeat(6,1fr)', gap:10}}>
        {SCORE_DIMENSIONS.map((d) => {
          const v = match[d.key] ?? 0;
          const color = v >= 90 ? 'var(--green-500)' : v >= 80 ? 'var(--orange-500)' : 'var(--ink-4)';
          const labelColor = v >= 90 ? 'var(--green-600)' : v >= 80 ? 'var(--orange-600)' : 'var(--ink-3)';
          return (
            <div key={d.key}>
              <div style={{display:'flex', justifyContent:'space-between', fontSize:10, fontWeight:600, marginBottom:4}}>
                <span style={{color:'var(--ink-2)'}}>{d.label}</span>
                <span className="mono" style={{color: labelColor}}>{v}</span>
              </div>
              <div className="bar"><span style={{width:`${v}%`, background: color}}/></div>
              <div className="muted" style={{fontSize:10, marginTop:4}}>{d.metaFor(match)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OrgRecommendations({ orgs }) {
  return (
    <div style={{padding:'16px 22px', borderTop:'1px solid var(--border-soft)', background:'var(--bg-sub)'}}>
      <div className="muted" style={{fontSize:11, fontWeight:700, marginBottom:8}}>추천 기관</div>
      <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:10}}>
        {orgs.map((o, i) => {
          const inner = (
            <>
              <span style={{width:36, height:36, borderRadius:10, background:'var(--orange-50)', color:'var(--orange-600)', display:'grid', placeItems:'center'}}>
                <span style={{width:20, height:20, display:'inline-block'}}>{Ic.org}</span>
              </span>
              <div style={{flex:1, minWidth:0}}>
                <div style={{fontSize:13, fontWeight:700}}>{o.name}</div>
                <div className="muted" style={{fontSize:11}}>매니저 {o.count}명 추천</div>
              </div>
              <div className="mono" style={{fontSize:18, fontWeight:800,
                color: i === 0 ? 'var(--orange-600)' : 'var(--green-600)'}}>{o.score}</div>
            </>
          );
          const baseStyle = {padding:14, display:'flex', alignItems:'center', gap:10};
          if (o.id) {
            return (
              <a key={o.name} href={`/organizations?id=${o.id}`}
                 className="card card-pad"
                 title={`${o.name} 상세 보기`}
                 style={{...baseStyle, color:'inherit', textDecoration:'none', cursor:'pointer'}}>
                {inner}
              </a>
            );
          }
          return (
            <div key={o.name} className="card card-pad" style={baseStyle}>
              {inner}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EmptyState({ caseRecord, candidatePool, onRegenerate, regenerating = false, canDecide = true }) {
  const status = caseRecord?.status;
  const assigned = status === 'assigned' || status === 'caring';
  const inMatching = status === 'matching';

  // 케이스는 매칭 단계인데 추천 후보가 0명. 원인이 두 가지로 갈리므로 서버가 준
  // candidate_pool(권역 가용 매니저 수 / 이미 제안된 수 / 남은 수)로 구분한다.
  //   · eligible === 0  → 진짜로 권역에 배정 가능한 매니저가 없음
  //   · remaining > 0   → 매니저는 있는데 후보 생성이 아직 안 돌았음(외부 임포트 등)
  //   · remaining === 0 → 가용 매니저를 이미 전부 제안했고 모두 처리(거절 등)된 상태
  // 예전에는 무조건 "권역에 매니저가 없습니다"로 안내해, 매니저가 25명 있는
  // 권역에서도 운영자가 매니저 부족으로 오진하게 만들었다.
  if (inMatching) {
    const eligible  = candidatePool?.eligible;
    const remaining = candidatePool?.remaining;
    const noManagers   = eligible === 0;
    const notGenerated = eligible > 0 && remaining > 0;

    const title = noManagers
      ? '배정 가능한 케어매니저가 없습니다.'
      : notGenerated
        ? '아직 추천 후보를 만들지 않았습니다.'
        : '더 이상 추천할 매니저가 없습니다.';

    const body = noManagers
      ? '현재 권역에 재직 중이면서 배정 가능한(가용·바쁨·이동중) 케어매니저가 없습니다. 케어매니저 등록 또는 근무 상태를 확인해 주세요.'
      : notGenerated
        ? `이 권역에는 배정 가능한 케어매니저가 ${eligible}명 있습니다. 아래 버튼으로 AI 추천 후보를 생성해 주세요.`
        : `배정 가능한 케어매니저 ${eligible}명을 모두 후보로 제안했고, 남은 후보가 없습니다. 매니저 가용 일정을 조정하거나 직접 배정을 검토해 주세요.`;

    return (
      <div className="card" style={{padding:'60px 22px', textAlign:'center'}}>
        <div style={{fontSize:48, marginBottom:10}}>{notGenerated ? '✨' : '🔍'}</div>
        <div style={{fontSize:16, fontWeight:700, marginBottom:6}}>{title}</div>
        <div className="muted" style={{fontSize:12.5, maxWidth:440, margin:'0 auto'}}>{body}</div>
        {canDecide && onRegenerate && (
          <button type="button" className={notGenerated ? "btn btn-primary btn-md" : "btn btn-soft btn-md"}
            onClick={onRegenerate} disabled={regenerating}
            style={{marginTop:16}}>
            {regenerating ? '다시 찾는 중…' : (notGenerated ? '추천 후보 생성 ↻' : '후보 다시 찾기 ↻')}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="card" style={{padding:'60px 22px', textAlign:'center'}}>
      <div style={{fontSize:48, marginBottom:10}}>✓</div>
      <div style={{fontSize:16, fontWeight:700, marginBottom:6}}>
        처리 대기 중인 매칭이 없습니다.
      </div>
      <div className="muted" style={{fontSize:12.5, maxWidth:420, margin:'0 auto'}}>
        {assigned
          ? '이 케이스는 이미 매니저가 배정되어 있어 추가 매칭이 필요하지 않습니다.'
          : "권역 대시보드의 케이스 흐름에서 '매칭' 단계에 있는 케이스를 선택해 주세요."}
      </div>
      <a href="/dashboard/region" className="btn btn-ghost btn-md" style={{marginTop:16, textDecoration:'none'}}>
        대시보드로 이동
      </a>
    </div>
  );
}

// ─── Pure helpers ────────────────────────────────────────────────────────

// Aggregate matches by organization for the bottom "추천 기관" panel.
// Top-2 by best match score per org. Carries the org id when the backend
// provides one so the row can deep-link into /organizations. Memoized at
// the caller.
function aggregateOrgs(matches) {
  const map = new Map();
  for (const m of matches) {
    const name = m.care_manager.organization_name;
    if (!name) continue;
    const id = m.care_manager.organization_id ?? null;
    const existing = map.get(name);
    if (existing) {
      existing.score = Math.max(existing.score, m.score);
      existing.count += 1;
      if (existing.id == null && id != null) existing.id = id;
    } else {
      map.set(name, { name, id, score: m.score, count: 1 });
    }
  }
  return [...map.values()].sort((a, b) => b.score - a.score).slice(0, 2);
}

export { COD };
